import React, { useMemo } from 'react';
import { Alert, Card, Space, Typography } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { RouteViewModelCarrierFinance, RouteViewModelFinance } from '../../../types/routeViewModel';

const { Text } = Typography;

const EMPTY_CARRIER_FINANCE: RouteViewModelCarrierFinance = {
  rateAmount: null,
  prepaymentPercent: null,
  mainPaymentPercent: null,
  prepaymentDelayBankDays: null,
  mainPaymentDelayBankDays: null,
  hasInvoiceTrigger: false,
  invoiceTriggerDate: null,
  paymentDueDate: null
};

function formatRub(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })} ₽`;
}

/** Сумма от ставки и % только для отображения */
function amountFromPercent(rate: number | null | undefined, percent: number | null | undefined): number | null {
  if (rate === null || rate === undefined || percent === null || percent === undefined) return null;
  const r = Number(rate);
  const p = Number(percent);
  if (!Number.isFinite(r) || !Number.isFinite(p)) return null;
  return Math.round((r * p) / 100 * 100) / 100;
}

function bankDaysLine(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const d = Math.max(0, Math.floor(Number(n)));
  const mod10 = d % 10;
  const mod100 = d % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${d} банковских дней`;
  if (mod10 === 1) return `${d} банковский день`;
  if (mod10 >= 2 && mod10 <= 4) return `${d} банковских дня`;
  return `${d} банковских дней`;
}

function percentWithAmount(percent: number | null | undefined, amountRub: number | null): string {
  if (percent === null || percent === undefined || Number.isNaN(Number(percent))) return '—';
  const p = Number(percent);
  if (amountRub != null && Number.isFinite(amountRub)) {
    return `${p}% (${formatRub(amountRub)})`;
  }
  return `${p}%`;
}

function formatDateFull(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function carrierPayableUiStatus(payable: RouteViewModelFinance['payable']): string {
  if (!payable) return '—';
  const required = Number(payable.totalRequired);
  if (!Number.isFinite(required) || required <= 0) return '—';
  const paid = Number(payable.totalPaid) || 0;
  const remaining = Number(payable.remaining);
  const eps = 0.01;
  if (Number.isFinite(remaining) && remaining <= eps) return 'Оплачено';
  if (paid <= eps) return 'Не оплачено';
  return 'Частично';
}

function calendarDueHint(dueDate: string | Date | null | undefined): string {
  if (dueDate === null || dueDate === undefined || dueDate === '') return '—';
  const due = dayjs(dueDate).startOf('day');
  if (!due.isValid()) return '—';
  const days = due.diff(dayjs().startOf('day'), 'day');
  if (days > 0) return `осталось ${days} дн.`;
  if (days < 0) return `просрочено на ${Math.abs(days)} дн.`;
  return 'срок сегодня';
}

export interface FinanceBlockProps {
  model?: RouteViewModelCarrierFinance | null;
  payable?: RouteViewModelFinance['payable'] | null;
  staleWarning?: boolean;
}

const FinanceBlock: React.FC<FinanceBlockProps> = ({ model, payable, staleWarning }) => {
  const m: RouteViewModelCarrierFinance = { ...EMPTY_CARRIER_FINANCE, ...(model ?? {}) };

  const { prepaymentAmount, mainPaymentAmount } = useMemo(() => {
    const prep = amountFromPercent(m.rateAmount, m.prepaymentPercent);
    const main = amountFromPercent(m.rateAmount, m.mainPaymentPercent);
    return { prepaymentAmount: prep, mainPaymentAmount: main };
  }, [m.rateAmount, m.prepaymentPercent, m.mainPaymentPercent]);

  const showTrigger = Boolean(m.hasInvoiceTrigger);

  return (
    <Card size="small" title={<Space><DollarOutlined />Финансы</Space>} style={{ marginBottom: 16 }}>
      {staleWarning && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="Финансовые данные изменились удалённо"
          description="Во время вашей работы произошла удалённая финансовая мутация. Перед сохранением обновите карточку, чтобы избежать устаревшего сохранения."
        />
      )}
      <div style={{ padding: 12, backgroundColor: '#fff7e6', borderRadius: 6 }}>
        <Text strong>Оплата перевозчику</Text>
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <Text type="secondary">Статус: </Text>
          <Text strong>{carrierPayableUiStatus(payable ?? null)}</Text>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.75 }}>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Ставка: </Text>
            <Text>{formatRub(m.rateAmount)}</Text>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Предоплата</Text>
            <div>{percentWithAmount(m.prepaymentPercent, prepaymentAmount)}</div>
            <div>
              <Text type="secondary">Отсрочка: </Text>
              <Text>{bankDaysLine(m.prepaymentDelayBankDays)}</Text>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>Доплата</Text>
            <div>{percentWithAmount(m.mainPaymentPercent, mainPaymentAmount)}</div>
            <div>
              <Text type="secondary">Отсрочка: </Text>
              <Text>{bankDaysLine(m.mainPaymentDelayBankDays)}</Text>
            </div>
          </div>

          <div
            style={{
              marginTop: 8,
              paddingTop: 10,
              borderTop: '1px solid #ffe7ba',
              fontSize: 13
            }}
          >
            {!showTrigger ? (
              <div>
                <Text type="secondary">Ожидается: </Text>
                <Text>получение счёта перевозчика — после загрузки счёта появятся дата отсчёта и срок оплаты.</Text>
              </div>
            ) : (
              <>
                <div>
                  <Text type="secondary">Дата события (получение счёта): </Text>
                  <Text>{formatDateFull(m.invoiceTriggerDate)}</Text>
                </div>
                <div>
                  <Text type="secondary">Срок оплаты: </Text>
                  <Text>{formatDateFull(m.paymentDueDate)}</Text>
                </div>
                <div>
                  <Text type="secondary">По сроку: </Text>
                  <Text>{calendarDueHint(m.paymentDueDate)}</Text>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default FinanceBlock;
